import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Button} from 'primereact/button';
import {Calendar} from 'primereact/calendar';
import {Dropdown} from 'primereact/dropdown';
import {InputSwitch} from 'primereact/inputswitch';
import {InputText} from 'primereact/inputtext';
import axios from 'axios';
import Cookies from 'js-cookie';
import moment from 'moment';

const COUNTRY_CODE = 'HU';
const getDictionaryName = (fieldName) => fieldName === 'segment'
  ? 'ntak-segment'
  : fieldName.replaceAll('_', '-');

const _FM_ReservationCountrySpecificComponent = forwardRef((props, ref) => {
  const {t, residentialUnitId, reservation, mode = 'edit', onEnabledChange} = props;
  const [enabled, setEnabled] = useState(false);
  const [fields, setFields] = useState([]);
  const [options, setOptions] = useState({});
  const [countryData, setCountryData] = useState({});
  const [saving, setSaving] = useState(false);

  const apiCall = useCallback((method, url, data) => axios({
    method,
    url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + url,
    data,
    params: {},
    headers: {
      'Authorization': 'Bearer ' + Cookies.get('userToken'),
      'Sys-name': Cookies.get('instanceId'),
      'Preferred-locale': Cookies.get('language')
    }
  }), []);

  const getUnitId = useCallback(() => residentialUnitId
    || reservation?.residential_unit?.id
    || reservation?.residential_unit
    || reservation?.berth?.residential_unit?.id, [residentialUnitId, reservation?.residential_unit, reservation?.berth]);

  const getAccommodationFromUnit = useCallback(async (unitId) => {
    const unitResponse = await apiCall('get', 'residential-unit/' + unitId);
    const unit = unitResponse?.data?.data;
    if (unit?.accommodation?.id || unit?.accommodation?.country) {
      return unit.accommodation;
    }
    const categoryId = unit?.residential_category?.id || unit?.residential_category;
    if (!categoryId) return null;
    const categoryResponse = await apiCall('get', 'residential-category/' + categoryId);
    return categoryResponse?.data?.data?.accommodation || null;
  }, [apiCall]);

  useEffect(() => {
    let active = true;
    const unitId = getUnitId();

    if (!unitId) {
      setEnabled(false);
      onEnabledChange?.(false);
      setFields([]);
      setOptions({});
      setCountryData({});
      return () => { active = false; };
    }

    (async () => {
      try {
        const accommodation = await getAccommodationFromUnit(unitId);
        const isHungarian = accommodation?.country?.toUpperCase() === COUNTRY_CODE;
        if (!active) return;
        setEnabled(isHungarian);
        onEnabledChange?.(isHungarian);
        if (!isHungarian) {
          setFields([]);
          setOptions({});
          setCountryData({});
          return;
        }

        const infoResponse = await apiCall('get', 'info/ReservationHu');
        const editableFields = (infoResponse?.data?.editable_fields || [])
          .filter((field) => field?.name && !['reservation', 'reservation_id'].includes(field.name));
        if (!active) return;
        setFields(editableFields);

        const enumFields = editableFields.filter((field) => field.name === 'segment' || field?.type?.includes('Enum'));
        const optionResponses = await Promise.all(enumFields.map(async (field) => {
          try {
            const response = await apiCall('get', 'dictionary/' + getDictionaryName(field.name));
            return [field.name, Object.values(response?.data?.data || {}).flat()];
          } catch (error) {
            return [field.name, []];
          }
        }));
        if (active) setOptions(Object.fromEntries(optionResponses));
      } catch (error) {
        if (active) {
          setEnabled(false);
          onEnabledChange?.(false);
          setFields([]);
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        }
      }
    })();

    return () => { active = false; };
  }, [apiCall, getAccommodationFromUnit, getUnitId, onEnabledChange, t]);

  useEffect(() => {
    if (!enabled || !reservation?.id) return;
    const countryRecord = reservation?.reservation_hus?.[0];
    if (!countryRecord?.id) {
      setCountryData({reservation: reservation.id});
      return;
    }
    apiCall('get', 'reservation-hu/' + countryRecord.id)
      .then((response) => setCountryData(response?.data?.data || {}))
      .catch((error) => {
        window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error.response);
      });
  }, [apiCall, enabled, reservation, t]);

  const saveForReservation = useCallback(async (reservationData = reservation) => {
    if (!enabled || !fields.length || !reservationData?.id) return null;
    const payload = {...countryData, reservation: reservationData.id};
    const response = countryData?.id
      ? await apiCall('patch', 'reservation-hu/' + countryData.id, payload)
      : await apiCall('post', 'reservation-hu', payload);
    setCountryData(response?.data?.data || payload);
    return response;
  }, [apiCall, countryData, enabled, fields.length, reservation]);

  useImperativeHandle(ref, () => ({saveForReservation, isEnabled: () => enabled}), [enabled, saveForReservation]);

  const save = async () => {
    try {
      setSaving(true);
      const response = await saveForReservation();
      if (response) window.App.toastShow('success', t('success'), response?.data?.message || t('success'));
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error.response);
    } finally {
      setSaving(false);
    }
  };

  const setValue = (name, value) => setCountryData((previous) => ({...previous, [name]: value}));
  const displayValue = (field) => {
    const value = countryData[field.name];
    if (value === true) return <i className="pi pi-check text-success"/>;
    if (value === false) return <i className="pi pi-times text-danger"/>;
    if (field.type?.includes('Date') && value) return moment.utc(value).local().format(t('YYYY-MM-DD HH:mm:ss'));
    const enumOption = (options[field.name] || []).find((option) => option.key === value || option.value === value);
    return enumOption ? window.App.getTranslatedLabel(enumOption.value ?? enumOption.key) : value;
  };

  const renderField = (field) => {
    if (mode === 'show') return <span className="p-d-block p-text-bold">{displayValue(field)}</span>;
    if (field.name === 'segment' || field.type?.includes('Enum')) {
      return <Dropdown options={options[field.name] || []} filter
                       optionLabel={(option) => window.App.getTranslatedLabel(option.value ?? option.key)}
                       optionValue={options[field.name]?.some((option) => Object.prototype.hasOwnProperty.call(option, 'key')) ? 'key' : 'value'}
                       id={field.name} emptyMessage={t('empty_message')}
                       onChange={(event) => setValue(field.name, event.value)} value={countryData[field.name]}/>;
    }
    if (field.type?.includes('Bool')) {
      return <InputSwitch id={field.name} checked={!!countryData[field.name]}
                          onChange={(event) => setValue(field.name, event.value)}/>;
    }
    if (field.type?.includes('Date')) {
      return <Calendar id={field.name} showIcon showTime={field.type.includes('DateTime')} hourFormat="24"
                       locale="default" dateFormat={t('YY-MM-DD')}
                       value={countryData[field.name] ? moment(countryData[field.name]).toDate() : null}
                       onChange={(event) => setValue(field.name, event.value)}/>;
    }
    return <InputText id={field.name}
                      keyfilter={field.type?.includes('Number') || field.type?.includes('Integer') || field.type?.includes('Float') ? 'num' : undefined}
                      onChange={(event) => setValue(field.name, event.target.value)} value={countryData[field.name] ?? ''}/>;
  };

  if (!enabled) return null;

  return (
    <>
      <h3 className="p-mt-0">{COUNTRY_CODE}</h3>
      {!fields.length && <p className="p-my-2">{t('no_country_specific_settings')}</p>}
      {!!fields.length &&
        <div className="p-grid">
          {fields.map((field) => (
            <div className="p-col-12 p-lg-6" key={field.name}>
              <div className="p-field p-fluid">
                <label htmlFor={field.name}>{t(field.name)}</label>
                <span className="p-d-block">{renderField(field)}</span>
              </div>
            </div>
          ))}
        </div>
      }
      {mode === 'edit' && !!fields.length &&
        <div className="p-grid">
          <div className="p-col p-text-right">
            <Button type="button" label={t('save')} icon="pi pi-save" loading={saving}
                    onClick={save} className="p-button-success"/>
          </div>
        </div>
      }
    </>
  );
});

export default withTranslation('common', {withRef: true})(_FM_ReservationCountrySpecificComponent);
